import { useState, useEffect, useCallback } from 'react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Star, 
  Settings, 
  Gift, 
  Plus, 
  Trash2, 
  Users,
  TrendingUp,
  Ticket,
  Euro,
  Package,
  Award,
  Edit2,
  Check,
  Info
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoyaltyTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  
  // Config state
  const [config, setConfig] = useState({
    enabled: false,
    points_per_euro: 1,
    min_order_for_points: 0,
    points_expiry_months: 12,
    welcome_bonus: 0,
    rewards: []
  });
  
  // Stats state
  const [stats, setStats] = useState({
    members_count: 0,
    total_points_circulation: 0,
    points_earned_this_month: 0,
    rewards_redeemed_this_month: 0,
    active_vouchers: 0
  });
  
  // Members state
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  
  // Dishes for reward selection
  const [dishes, setDishes] = useState([]);
  
  // Modal state
  const [rewardModal, setRewardModal] = useState({ open: false, data: null, isEdit: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, rewardId: null, rewardName: '' });

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/loyalty/config`);
      setConfig(response.data);
    } catch (err) {
      toast.error('Erreur lors du chargement de la configuration');
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/loyalty/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      setLoadingMembers(true);
      const response = await axios.get(`${API}/loyalty/members`);
      setMembers(response.data);
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  const fetchDishes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/menu/dishes`);
      setDishes(response.data || []);
    } catch (err) {
      console.error('Error fetching dishes:', err);
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchStats(), fetchDishes()]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchConfig, fetchStats, fetchDishes]);

  useEffect(() => {
    if (activeTab === 'members') {
      fetchMembers();
    }
  }, [activeTab, fetchMembers]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/loyalty/config`, {
        enabled: config.enabled,
        points_per_euro: parseFloat(config.points_per_euro) || 1,
        min_order_for_points: parseFloat(config.min_order_for_points) || 0,
        points_expiry_months: config.points_expiry_months ? parseInt(config.points_expiry_months) : null,
        welcome_bonus: parseInt(config.welcome_bonus) || 0
      });
      toast.success('Configuration sauvegardée');
      fetchConfig();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const openRewardModal = (reward = null) => {
    if (reward) {
      setRewardModal({
        open: true,
        isEdit: true,
        data: { ...reward }
      });
    } else {
      setRewardModal({
        open: true,
        isEdit: false,
        data: {
          id: crypto.randomUUID(),
          name: '',
          type: 'discount',
          discount_amount: 5,
          product_id: '',
          product_name: '',
          points_cost: 100,
          is_active: true
        }
      });
    }
  };

  const handleSaveReward = async () => {
    const reward = rewardModal.data;
    
    if (!reward.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (reward.points_cost <= 0) {
      toast.error('Le coût en points doit être positif');
      return;
    }
    if (reward.type === 'discount' && (!reward.discount_amount || reward.discount_amount <= 0)) {
      toast.error('Le montant de réduction doit être positif');
      return;
    }
    if (reward.type === 'free_product' && !reward.product_id) {
      toast.error('Veuillez sélectionner un produit');
      return;
    }

    setSaving(true);
    try {
      if (rewardModal.isEdit) {
        // Update: save the whole config with updated reward
        const updatedRewards = config.rewards.map(r => 
          r.id === reward.id ? reward : r
        );
        await axios.put(`${API}/loyalty/config`, {
          rewards: updatedRewards
        });
        toast.success('Récompense modifiée');
      } else {
        // Add new reward
        await axios.post(`${API}/loyalty/config/rewards`, reward);
        toast.success('Récompense ajoutée');
      }
      setRewardModal({ open: false, data: null, isEdit: false });
      fetchConfig();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReward = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/loyalty/config/rewards/${deleteModal.rewardId}`);
      toast.success('Récompense supprimée');
      setDeleteModal({ open: false, rewardId: null, rewardName: '' });
      fetchConfig();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la suppression'));
    } finally {
      setSaving(false);
    }
  };

  const handleProductSelect = (productId) => {
    const product = dishes.find(d => d.id === productId);
    if (product) {
      setRewardModal({
        ...rewardModal,
        data: {
          ...rewardModal.data,
          product_id: productId,
          product_name: product.name
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Points de fidélité</h2>
          <p className="text-sm text-gray-500">Récompensez vos clients réguliers avec des points</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">Programme</span>
          <Switch
            checked={config.enabled}
            onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
          />
          <span className={`text-sm font-medium ${config.enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
            {config.enabled ? 'Actif' : 'Inactif'}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.members_count}</p>
                <p className="text-xs text-gray-500">Membres</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_points_circulation}</p>
                <p className="text-xs text-gray-500">Points en circulation</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.points_earned_this_month}</p>
                <p className="text-xs text-gray-500">Points ce mois</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Gift className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.rewards_redeemed_this_month}</p>
                <p className="text-xs text-gray-500">Récompenses ce mois</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active_vouchers}</p>
                <p className="text-xs text-gray-500">Bons actifs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="rewards" className="flex items-center gap-2">
              <Gift className="w-4 h-4" />
              Récompenses
            </TabsTrigger>
            <TabsTrigger value="members" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Membres
            </TabsTrigger>
          </TabsList>

          {/* Configuration Tab */}
          <TabsContent value="config" className="mt-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 max-w-2xl">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Paramètres du programme</h2>
              
              <div className="space-y-6">
                {/* Points per Euro */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    Points par euro dépensé
                  </Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={config.points_per_euro}
                      onChange={(e) => setConfig({ ...config, points_per_euro: e.target.value })}
                      className="w-32"
                    />
                    <span className="text-gray-500">point(s) / €</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Ex: Un client qui dépense 25€ gagnera {Math.round(25 * (config.points_per_euro || 1))} points
                  </p>
                </div>

                {/* Min Order */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Euro className="w-4 h-4 text-gray-500" />
                    Montant minimum pour gagner des points
                  </Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={config.min_order_for_points}
                      onChange={(e) => setConfig({ ...config, min_order_for_points: e.target.value })}
                      className="w-32"
                    />
                    <span className="text-gray-500">€</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    0 = pas de minimum
                  </p>
                </div>

                {/* Welcome Bonus */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-purple-500" />
                    Bonus de bienvenue
                  </Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Input
                      type="number"
                      min="0"
                      value={config.welcome_bonus}
                      onChange={(e) => setConfig({ ...config, welcome_bonus: e.target.value })}
                      className="w-32"
                    />
                    <span className="text-gray-500">points offerts à la 1ère commande</span>
                  </div>
                </div>

                {/* Expiry */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-gray-500" />
                    Expiration des points
                  </Label>
                  <Select
                    value={config.points_expiry_months?.toString() || "never"}
                    onValueChange={(val) => setConfig({ 
                      ...config, 
                      points_expiry_months: val === "never" ? null : parseInt(val) 
                    })}
                  >
                    <SelectTrigger className="w-48 mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">Après 3 mois d'inactivité</SelectItem>
                      <SelectItem value="6">Après 6 mois d'inactivité</SelectItem>
                      <SelectItem value="12">Après 12 mois d'inactivité</SelectItem>
                      <SelectItem value="24">Après 24 mois d'inactivité</SelectItem>
                      <SelectItem value="never">Jamais</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleSaveConfig} 
                  disabled={saving}
                  className="bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                >
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards" className="mt-6 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Récompenses disponibles</h2>
                <p className="text-sm text-gray-500">Définissez les récompenses que vos clients peuvent obtenir</p>
              </div>
              <Button onClick={() => openRewardModal()} className="bg-emerald-500 hover:bg-emerald-600 rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle récompense
              </Button>
            </div>

            {config.rewards.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <Gift className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Aucune récompense</h3>
                <p className="text-gray-500 mb-4">Créez des récompenses pour motiver vos clients</p>
                <Button onClick={() => openRewardModal()} variant="outline" className="rounded-xl">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une récompense
                </Button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {config.rewards.map(reward => (
                  <div 
                    key={reward.id}
                    className={`bg-white rounded-2xl border p-5 ${
                      reward.is_active ? 'border-amber-200' : 'border-gray-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {reward.type === 'discount' ? (
                          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                            <Euro className="w-5 h-5 text-emerald-600" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                            <Package className="w-5 h-5 text-purple-600" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-gray-900">{reward.name}</h3>
                          <p className="text-xs text-gray-500">
                            {reward.type === 'discount' 
                              ? `${reward.discount_amount}€ de réduction`
                              : reward.product_name || 'Produit offert'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Badge className="bg-amber-100 text-amber-700">
                        <Star className="w-3 h-3 mr-1" />
                        {reward.points_cost} pts
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => openRewardModal(reward)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => setDeleteModal({ 
                            open: true, 
                            rewardId: reward.id, 
                            rewardName: reward.name 
                          })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="mt-6">
            <div className="bg-white rounded-2xl border border-gray-100">
              <div className="p-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Membres du programme</h2>
                <p className="text-sm text-gray-500">Clients ayant accumulé des points</p>
              </div>
              
              {loadingMembers ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500" />
                </div>
              ) : members.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  Aucun membre pour le moment
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {members.map(member => (
                    <div key={member.client_id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {member.client_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{member.client_name}</p>
                          <p className="text-sm text-gray-500">{member.client_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-amber-600 flex items-center gap-1">
                          <Star className="w-4 h-4" />
                          {member.current_points} pts
                        </p>
                        <p className="text-xs text-gray-400">
                          Total: {member.lifetime_points} pts
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

      {/* Reward Modal */}
      <Dialog open={rewardModal.open} onOpenChange={(open) => !open && setRewardModal({ open: false, data: null, isEdit: false })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-500" />
              {rewardModal.isEdit ? 'Modifier la récompense' : 'Nouvelle récompense'}
            </DialogTitle>
          </DialogHeader>
          
          {rewardModal.data && (
            <div className="space-y-5">
              {/* Name */}
              <div>
                <Label>Nom de la récompense *</Label>
                <Input
                  value={rewardModal.data.name}
                  onChange={(e) => setRewardModal({
                    ...rewardModal,
                    data: { ...rewardModal.data, name: e.target.value }
                  })}
                  placeholder="Ex: Café offert, 5€ de réduction..."
                  className="mt-1.5"
                />
              </div>

              {/* Type */}
              <div>
                <Label>Type de récompense</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setRewardModal({
                      ...rewardModal,
                      data: { ...rewardModal.data, type: 'discount' }
                    })}
                    className={`p-4 rounded-xl border-2 text-center ${
                      rewardModal.data.type === 'discount'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Euro className="w-6 h-6 mx-auto mb-2" />
                    <p className="font-medium">Réduction</p>
                    <p className="text-xs text-gray-500">Montant déduit</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRewardModal({
                      ...rewardModal,
                      data: { ...rewardModal.data, type: 'free_product' }
                    })}
                    className={`p-4 rounded-xl border-2 text-center ${
                      rewardModal.data.type === 'free_product'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Package className="w-6 h-6 mx-auto mb-2" />
                    <p className="font-medium">Produit offert</p>
                    <p className="text-xs text-gray-500">Un article gratuit</p>
                  </button>
                </div>
              </div>

              {/* Discount Amount or Product */}
              {rewardModal.data.type === 'discount' ? (
                <div>
                  <Label>Montant de la réduction (€)</Label>
                  <Input
                    type="number"
                    min="1"
                    step="0.5"
                    value={rewardModal.data.discount_amount || ''}
                    onChange={(e) => setRewardModal({
                      ...rewardModal,
                      data: { ...rewardModal.data, discount_amount: parseFloat(e.target.value) || 0 }
                    })}
                    className="mt-1.5"
                  />
                </div>
              ) : (
                <div>
                  <Label>Produit offert</Label>
                  <Select
                    value={rewardModal.data.product_id || ''}
                    onValueChange={handleProductSelect}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Sélectionner un produit..." />
                    </SelectTrigger>
                    <SelectContent>
                      {dishes.map(dish => (
                        <SelectItem key={dish.id} value={dish.id}>
                          {dish.name} ({dish.price?.toFixed(2)}€)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {rewardModal.data.product_name && (
                    <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      {rewardModal.data.product_name}
                    </p>
                  )}
                </div>
              )}

              {/* Points Cost */}
              <div>
                <Label className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  Coût en points
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={rewardModal.data.points_cost}
                  onChange={(e) => setRewardModal({
                    ...rewardModal,
                    data: { ...rewardModal.data, points_cost: parseInt(e.target.value) || 0 }
                  })}
                  className="mt-1.5"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Avec {config.points_per_euro} pt/€, cela représente ~{Math.round(rewardModal.data.points_cost / (config.points_per_euro || 1))}€ de commandes
                </p>
              </div>

              {/* Active */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900">Récompense active</p>
                  <p className="text-xs text-gray-500">Visible et échangeable par les clients</p>
                </div>
                <Switch
                  checked={rewardModal.data.is_active}
                  onCheckedChange={(checked) => setRewardModal({
                    ...rewardModal,
                    data: { ...rewardModal.data, is_active: checked }
                  })}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRewardModal({ open: false, data: null, isEdit: false })}>
              Annuler
            </Button>
            <Button onClick={handleSaveReward} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
              {saving ? 'Sauvegarde...' : rewardModal.isEdit ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, rewardId: null, rewardName: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer la récompense <strong>"{deleteModal.rewardName}"</strong> ? 
            Les bons déjà générés resteront valides.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, rewardId: null, rewardName: '' })}>
              Annuler
            </Button>
            <Button onClick={handleDeleteReward} disabled={saving} className="bg-red-500 hover:bg-red-600">
              {saving ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
